import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import BackButton from '../components/BackButton';
import SaveButton from '../components/SaveButton';
import { 
  getIntegrationsStatus, 
  initiateIntegrationConnection, 
  disconnectIntegrationConnection,
  getIntegrationDisplayName,
  isIntegrationConfigured 
} from '../services/integrationService';
import { 
  getPillarsSettings, 
  updatePillarsSettings, 
  getPillarDisplayName, 
  getPillarIcon 
} from '../services/pillarsService';
import { useAuth } from '../contexts/AuthContext';
import { getInternalUsers } from '../services/userService';
import PortalBackground from '../components/PortalBackground';

const Settings = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('company');

  // Integrations State
  const [integrationsStatus, setIntegrationsStatus] = useState({});
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [connectingIntegration, setConnectingIntegration] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configIntegration, setConfigIntegration] = useState(null);
  const [error, setError] = useState(null);

  // Pillars State
  const [pillarsSettings, setPillarsSettings] = useState({});
  const [pillarsLoading, setPillarsLoading] = useState(true);
  const [pillarsChanged, setPillarsChanged] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);

  useEffect(() => {
    fetchIntegrationsStatus();
    if (currentUser) {
      fetchPillarsSettings();
      fetchInternalUsers();
    }
  }, [currentUser]);

  const fetchIntegrationsStatus = async () => {
    try {
      setIntegrationsLoading(true);
      const status = await getIntegrationsStatus();
      setIntegrationsStatus(status);
    } catch (error) {
      console.error('Error fetching integrations status:', error);
      setError('Failed to fetch integrations status');
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const fetchPillarsSettings = async () => {
    try {
      setPillarsLoading(true);
      const settings = await getPillarsSettings(currentUser.uid);
      setPillarsSettings(settings);
    } catch (error) {
      console.error('Error fetching pillars settings:', error);
      setError('Failed to fetch pillars settings');
    } finally {
      setPillarsLoading(false);
    }
  };

  const fetchInternalUsers = async () => {
    try {
      const users = await getInternalUsers();
      setInternalUsers(users);
    } catch (error) {
      console.error('Error fetching internal users:', error);
      setError('Failed to fetch internal users');
      setInternalUsers([]); // Set empty array as fallback
    }
  };

  const handleConnectIntegration = async (integrationKey) => {
    try {
      setConnectingIntegration(integrationKey);
      
      if (!isIntegrationConfigured(integrationKey)) {
        // Show a coming soon message for unconfigured integrations
        setError(`${getIntegrationDisplayName(integrationKey)} integration is coming soon! This feature will be available in a future update.`);
        return;
      }

      await initiateIntegrationConnection(integrationKey);
    } catch (error) {
      console.error('Error connecting integration:', error);
      setError(`Failed to connect ${getIntegrationDisplayName(integrationKey)}: ${error.message}`);
    } finally {
      setConnectingIntegration(null);
    }
  };

  const handleDisconnectIntegration = async (integrationKey) => {
    try {
      await disconnectIntegrationConnection(integrationKey);
      await fetchIntegrationsStatus(); // Refresh status
      setError(null);
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      setError(`Failed to disconnect ${getIntegrationDisplayName(integrationKey)}: ${error.message}`);
    }
  };

  const handlePillarSettingChange = (pillar, field, value) => {
    setPillarsSettings(prev => ({
      ...prev,
      [pillar]: {
        ...prev[pillar],
        [field]: value
      }
    }));
    setPillarsChanged(true);
  };

  const handleSave = async () => {
    if (activeTab === 'pillars' && pillarsChanged && currentUser) {
      try {
        await updatePillarsSettings(currentUser.uid, pillarsSettings);
        setPillarsChanged(false);
        setError(null);
        // Show success message
        console.log('Pillars settings saved successfully');
      } catch (error) {
        console.error('Error saving pillars settings:', error);
        setError('Failed to save pillars settings');
      }
    } else {
      // TODO: Implement save functionality for other tabs
      console.log('Saving settings');
    }
  };

  const renderCompanySettings = () => (
    <div className="space-y-6">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 tracking-wide uppercase">Company Settings</h2>
        </div>
        <div className="space-y-4">
          <button
            onClick={() => navigate('/crm/internal-users')}
            className="flex items-center px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl w-full transition-colors"
          >
            <Users className="h-5 w-5 mr-3 text-gray-600 dark:text-gray-400" />
            <div className="text-left">
              <h3 className="font-medium text-gray-900 dark:text-white">Internal Users</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage internal user accounts and permissions</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderPillarsSettings = () => {
    const pillars = ['sales', 'operations', 'finance', 'technology', 'marketing', 'hr'];

    if (pillarsLoading) {
      return (
        <div className="space-y-6">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 tracking-wide uppercase">Pillars Settings</h2>
            </div>
            <div className="flex items-center justify-center h-32">
              <BBALoader size={32} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 tracking-wide uppercase">Pillars Settings</h2>
          </div>
          <div className="space-y-6">
            {pillars.map((pillar) => (
              <div key={pillar} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">{getPillarIcon(pillar)}</span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {getPillarDisplayName(pillar)}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Schedule Link
                    </label>
                    <input
                      type="url"
                      value={pillarsSettings[pillar]?.scheduleLink || ''}
                      onChange={(e) => handlePillarSettingChange(pillar, 'scheduleLink', e.target.value)}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      placeholder="https://calendly.com/example/pillar-1on1"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Link for scheduling calls with {getPillarDisplayName(pillar)} team
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Question Assignee
                    </label>
                                         <select
                       value={pillarsSettings[pillar]?.questionAssignee || ''}
                       onChange={(e) => handlePillarSettingChange(pillar, 'questionAssignee', e.target.value)}
                       className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                     >
                       <option value="">Select an internal user</option>
                       {internalUsers.length === 0 ? (
                         <option value="" disabled>No internal users found</option>
                       ) : (
                         internalUsers.map((user) => (
                           <option key={user.id} value={user.id}>
                             {user.name}
                           </option>
                         ))
                       )}
                     </select>
                                         <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                       Internal user to assign questions about {getPillarDisplayName(pillar)}
                     </p>

                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrations = () => {
    const integrations = [
      {
        key: 'twilio',
        name: 'Twilio',
        description: 'Connect with Twilio for SMS and voice capabilities',
        icon: '/twillio-icon.svg',
      },

      {
        key: 'gdrive',
        name: 'Google Drive',
        description: 'Connect Google Drive to access your files and documents',
        icon: '/gdrive-icon.svg',
      },
      {
        key: 'quickbooks',
        name: 'QuickBooks',
        description: 'Sync financial data and accounting information',
        icon: '/qbooks-icon.svg',
      },
      {
        key: 'zapier',
        name: 'Zapier',
        description: 'Automate workflows and connect apps with Zapier',
        icon: '/zapier-icon.svg',
      },
      {
        key: 'googleanalytics',
        name: 'Google Analytics',
        description: 'Connect Google Analytics to track your website performance',
        icon: '/gdata-icon.svg',
      },
      {
        key: 'stripe',
        name: 'Stripe',
        description: 'Process payments and manage billing with Stripe',
        icon: '/stripe-logo.svg',
      },
      {
        key: 'meta',
        name: 'Meta',
        description: 'Connect your Meta account to track ad performance',
        icon: '/meta-icon.svg',
      },
      {
        key: 'docusign',
        name: 'DocuSign',
        description: 'Integrate with DocuSign for electronic signatures',
        icon: '/docsign-icon.svg',
      },
      {
        key: 'youtube',
        name: 'YouTube',
        description: 'Connect your YouTube channel to manage your videos',
        icon: '/youtube-icon.svg',
      },
      {
        key: 'activecampaign',
        name: 'ActiveCampaign',
        description: 'Sync contacts and manage email marketing with ActiveCampaign',
        icon: '/activecamp-icon.svg',
      },
      {
        key: 'googlemaps',
        name: 'Google Maps API',
        description: 'Integrate Google Maps for location-based services',
        icon: '/gmaps-icon.svg',
      },
    ];

    if (integrationsLoading) {
      return (
        <div className="space-y-6">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 tracking-wide uppercase">Integrations</h2>
            </div>
            <div className="flex items-center justify-center h-32">
              <BBALoader size={32} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 tracking-wide uppercase">Integrations</h2>
          </div>
          <div className="space-y-4">
            {integrations.map((integration) => {
              const status = integrationsStatus[integration.key] || { connected: false, connectedAt: null };
              const isConnecting = connectingIntegration === integration.key;
              const isConfigured = isIntegrationConfigured(integration.key);

              return (
                <div key={integration.key} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img src={integration.icon} alt={integration.name} className="w-8 h-8" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{integration.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{integration.description}</p>
                      {status.connected && status.connectedAt && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Connected {new Date(status.connectedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status.connected ? (
                      <>
                        <div className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-md text-sm font-medium">
                          Active
                        </div>
                        <button
                          onClick={() => handleDisconnectIntegration(integration.key)}
                          className="px-3 py-1 text-red-600 border border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900 transition-colors text-sm"
                        >
                          Disconnect
                        </button>
                      </>
                    ) : !isIntegrationConfigured(integration.key) ? (
                      <button
                        onClick={() => handleConnectIntegration(integration.key)}
                        className="px-4 py-2 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-600 rounded-md hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors text-sm font-medium cursor-not-allowed"
                        disabled
                      >
                        Coming Soon
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnectIntegration(integration.key)}
                        disabled={true}
                        className="px-4 py-2 bg-gray-300 text-gray-500 border border-gray-300 rounded-md cursor-not-allowed text-sm font-medium"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
    setConfigIntegration(null);
    setConnectingIntegration(null);
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm')} />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Settings</h1>
          <SaveButton onClick={handleSave} disabled={!pillarsChanged} />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('company')}
              className={`${
                activeTab === 'company'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Company Settings
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`${
                activeTab === 'integrations'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Integrations
            </button>
            <button
              onClick={() => setActiveTab('pillars')}
              className={`${
                activeTab === 'pillars'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Pillars
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'company' && renderCompanySettings()}
        {activeTab === 'integrations' && renderIntegrations()}
        {activeTab === 'pillars' && renderPillarsSettings()}

        {/* Configuration Modal */}
        {showConfigModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeConfigModal}></div>
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-2xl w-full mx-4 z-50 p-6 relative">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Configure {getIntegrationDisplayName(configIntegration)}
                </h2>
                <button onClick={closeConfigModal} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-xl font-bold">&times;</button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300">
                  To connect {getIntegrationDisplayName(configIntegration)}, you need to configure the following environment variables:
                </p>
                
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Required Environment Variables:</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-sm">
                        REACT_APP_{configIntegration?.toUpperCase()}_CLIENT_ID
                      </code>
                      <span className="text-sm text-gray-500">- Your OAuth Client ID</span>
                    </div>

                    {configIntegration === 'twilio' && (
                      <>
                        <div className="flex items-center gap-2">
                          <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-sm">
                            TWILIO_CLIENT_SECRET
                          </code>
                          <span className="text-sm text-gray-500">- Your Twilio Client Secret (Firebase Functions)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-sm">
                            FRONTEND_URL
                          </code>
                          <span className="text-sm text-gray-500">- Your frontend URL (Firebase Functions)</span>
                        </div>
                      </>
                    )}
                    {configIntegration === 'googleCalendar' || configIntegration === 'gmail' || configIntegration === 'gdrive' || configIntegration === 'googleanalytics' || configIntegration === 'googlemaps' || configIntegration === 'youtube' && (
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-sm">
                          GOOGLE_CLIENT_SECRET
                        </code>
                        <span className="text-sm text-gray-500">- Your Google Client Secret (Firebase Functions)</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-md">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Setup Instructions:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-200">
                    <li>Create an OAuth application in your {getIntegrationDisplayName(configIntegration)} developer console</li>
                    <li>Add the environment variables to your <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">.env</code> file</li>
                    <li>For Firebase Functions, set the secrets using: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">firebase functions:config:set</code></li>
                    <li>Deploy your Firebase Functions: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">firebase deploy --only functions</code></li>
                    <li>Restart your development server</li>
                  </ol>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={closeConfigModal}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeConfigModal();
                      // You could add a link to documentation here
                      window.open('https://your-docs-url.com/integrations', '_blank');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    View Documentation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings; 